import { createElement } from "react";
import { type_GM_STATE } from "../../../../helpers"
import { useApp } from "../../../hooks/useApp"
import { getActiveLengthByMainProductVariantId, getTotalPrice, getMainProductVariantById, getSelectedPurchasableMainProductVariant } from "../../../utils"
import { formatCentsToDollars } from "../../../utils/formatCentsToDollars"
import { Text } from "../../ui/Text/Text"
import { ChainVariants } from "../ChainVariants/ChainVariants"
import { MetalToggler } from "../MetalToggler/MetalToggler"

declare var GM_STATE: type_GM_STATE

const getBiLineWithLength = (biLine: string, length: string) => {
  if (!length) {
    return biLine
  }

  if (biLine.toLowerCase().includes('length')) {
    return biLine
  }

  return biLine ? `${biLine} Length ${length}` : `Length ${length}`
}

export const Header = () => {
  const { 
    mainProduct, 
    activeMainProductVariantId, 
    activePendantProductVariantIds, 
    isMainProductSinglePendant,
    isCarabinerProductMode
  } = useApp()
  const  { title, metafields: { pdpBiLine } } = mainProduct
  const activeProductVariant = getMainProductVariantById(mainProduct, activeMainProductVariantId)
  const selectedPurchasableVariant = getSelectedPurchasableMainProductVariant(
    mainProduct,
    activeMainProductVariantId,
    activePendantProductVariantIds,
    isMainProductSinglePendant
  ) || activeProductVariant
  const pdpVariantBiLine = activeProductVariant.metafields.pdpBiLine
  const currentProductBiLine = pdpVariantBiLine ? pdpVariantBiLine : pdpBiLine
  const activeLength = mainProduct.optionsWithValues.some((option) => option.name === 'Length')
    ? getActiveLengthByMainProductVariantId(mainProduct, activeMainProductVariantId)
    : ''
  const currentProductBiLineWithLength = getBiLineWithLength(currentProductBiLine, activeLength)
  const totalPrice = getTotalPrice(mainProduct, activeMainProductVariantId, activePendantProductVariantIds)

  const renderWishlistButton = (className: string) => {
    if (!mainProduct.globalSettings.enableBauCtas || !GM_STATE.wishlist.enable) {
      return null
    }

    return createElement('wishlist-button', {
      className,
      product: JSON.stringify(mainProduct),
      'product-bi-line': currentProductBiLineWithLength,
      'variant-selected': JSON.stringify(selectedPurchasableVariant),
      'variant-selected-id': String(selectedPurchasableVariant.id),
      'template-suffix': mainProduct.templateSuffix,
      disabled: 'false',
      'error-message': '',
      'is-show-only-icon': 'true',
      'product-personalised-price': '0',
      'use-product-slideout': 'true'
    })
  }

  const renderVariantSelectors = () => {
    if (isMainProductSinglePendant || isCarabinerProductMode) {
      return null
    }

    return (
      <div className="hidden w-full mt-5 mb-[-25px] xl:block">
        <MetalToggler showLabel />
        <ChainVariants showLabel />
      </div>
    )
  }

  // @TODO. Remove, when wishlist normalization is fixed
  // https://scriptive-dev.atlassian.net/browse/SNS-980 
  const disableWishlist = mainProduct.id === 7779675406432 || mainProduct.id === 7779675439200

  return (
    <>
      <header className="absolute left-0 right-0 top-0 flex flex-col gap-1 items-center p-4 xl:hidden">
        <div className="relative flex w-full items-start justify-center">
          <h1 className="text-center">
            <Text className="capitalize" style="h6">{title}</Text>
          </h1>

          {!disableWishlist && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 leading-none">
              {renderWishlistButton('wishlist-button js-wishlist-button js-scroll-to-position block')}
            </div>
          )}
        </div>

        <p className="block mt-[2px] text-subdued text-[12px]">
          {currentProductBiLineWithLength}
        </p>

        {(isMainProductSinglePendant || isCarabinerProductMode) && (
          <Text className="ProductMeta__Price Price js-product-price text-[13px]">{formatCentsToDollars(totalPrice, mainProduct.tags)}</Text>
        )}

        {renderVariantSelectors()}

      </header>

      <header className="flex flex-col items-start xl-max:hidden">
        <div className="flex w-full items-start justify-between gap-4">
          <h1><Text className="text-[18px] capitalize" style="h4">{title}</Text></h1>
          {!disableWishlist && (
            <div className="shrink-0 leading-none">
              {renderWishlistButton('wishlist-button js-wishlist-button js-scroll-to-position block')}
            </div>
          )}
        </div>

        <div className="flex flex-col justify-between gap-2">
          <p className="block mt-XS text-subdued">
            {currentProductBiLineWithLength}
          </p>

          <span
            className="ProductMeta__Price Price js-product-price"
            aria-live="polite"
            aria-atomic="true"
          >
            {formatCentsToDollars(totalPrice, mainProduct.tags)}
          </span>
        </div>

        {renderVariantSelectors()}
      </header>
    </>
  )
}